import React from "react";
import { BASE_COLUMN_TYPES, BaseColumnType } from "../../columns/config/types";
import { ReportColumnConfigBase } from "../../columns/config/ReportColumnConfig";
import { formatMoneyCurrency, formatPercent } from "../../../../utils";
import { StatusColumn } from "../common";

const ORepColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Avatar> = {
  header: "O Rep",
  type: BASE_COLUMN_TYPES.Avatar,
  fields: [
    "sales_rep_user_id",
    "sales_rep_first_name",
    "sales_rep_last_name",
    "sales_rep_avatar_file_id",
  ],
  accessorKey: "sales_rep",
  canSort: true,
  orderByField: "sales_rep_last_name",
  width: 73,
  dataTransform: ({
    sales_rep_first_name,
    sales_rep_last_name,
    sales_rep_avatar_file_id,
  }) => ({
    full_name: `${sales_rep_first_name} ${sales_rep_last_name}`,
    avatar_file_id: sales_rep_avatar_file_id,
  }),
};

const CRepColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Avatar> = {
  header: "C Rep",
  type: BASE_COLUMN_TYPES.Avatar,
  fields: [
    "client_rep_user_id",
    "client_rep_first_name",
    "client_rep_last_name",
    "client_rep_avatar_file_id",
  ],
  accessorKey: "client_rep",
  canSort: true,
  orderByField: "client_rep_last_name",
  width: 73,
  dataTransform: ({
    client_rep_first_name,
    client_rep_last_name,
    client_rep_avatar_file_id,
  }) => ({
    full_name: `${client_rep_first_name} ${client_rep_last_name}`,
    avatar_file_id: client_rep_avatar_file_id,
  }),
};

const ProjectNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: "Project",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["job_number"],
  accessorKey: "job_number",
  canSort: true,
  orderByField: "job_number",
  width: 83,
  dataTransform: ({ job_number }) => ({
    href: `/project/${job_number}`,
    text: `#${job_number}`,
  }),
};

const ProjectNameColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Link> =
{
  header: "Name",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["job_name", "job_number"],
  accessorKey: "job_name",
  canSort: true,
  orderByField: "job_name",
  width: 150,
 textOptions: {
    truncate: true,
 },
  dataTransform: ({ job_name, job_number }) => ({
    href: `/project/${job_number}`,
    text: job_name,
  }),
};

const ClientColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Link> = {
  header: "Client",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["client_id", "client_name"],
  accessorKey: "client",
  canSort: true,
  orderByField: "client_name",
  width: 136,
 textOptions: {
    truncate: true,
 },
  dataTransform: ({ client_id, client_name }) => ({
    href: `/client.php?id=${client_id}`,
    text: client_name,
  }),
  // linkAction: ({ client_id }) => console.log(client_id), // change to open clientSidePanel
};

const DateColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Date> = {
  header: "Date",
  type: BASE_COLUMN_TYPES.Date,
  fields: ["date_orderdate"],
  accessorKey: "order_date",
  canSort: true,
  orderByField: "date_orderdate",
  width: 120,
};

const BudgetColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Money> = {
  header: "Budget",
  type: BASE_COLUMN_TYPES.Money,
  fields: ["total_budget", "currency_id"],
  accessorKey: "budget",
  canSort: true,
  orderByField: "total_budget",
  width: 100,
  moneyOptions: { decimalPlaces: 0 },
  dataTransform: ({ total_budget, currency_id }) => ({
    amount: total_budget,
    currency_id,
  }),
};

const SubtotalColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Money> = {
  header: "Subtotal",
  type: BASE_COLUMN_TYPES.Money,
  fields: ["total_subtotal", "currency_id"],
  accessorKey: "subtotal",
  canSort: true,
  orderByField: "total_subtotal",
  width: 95,
  moneyOptions: { decimalPlaces: 2 },
  dataTransform: ({ total_subtotal, currency_id }) => ({
    amount: total_subtotal,
    currency_id,
  }),
};

const MarginColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Money
> = {
  header: "Margin",
  type: BASE_COLUMN_TYPES.Money,
  fields: ["order_type", "booked_margin_amount", "billed_margin_amount", "currency_id", "is_billed"],
  accessorKey: "margin_amount",
  canSort: true,
  orderByField: "margin_amount",
  moneyOptions: { decimalPlaces: 2 },
  width: 93,
  dataTransform: ({ order_type, booked_margin_amount, billed_margin_amount, currency_id, is_billed }) => {
    const margin_amount = "INVOICE" === order_type && is_billed ?
      billed_margin_amount :
      booked_margin_amount;

    const tooltipText = (billed_margin_amount !== null || booked_margin_amount !== null) && (
      <table>
        <tbody>
          {billed_margin_amount !== null && (
            <tr>
              <td>Billed Margin:</td>
              <td align="right">{formatMoneyCurrency(billed_margin_amount, currency_id, 2)}</td>
              {!is_billed && <td>(Partial)</td>}
            </tr>
          )}
          {booked_margin_amount !== null && <tr>
            <td>Booked Margin:</td>
            <td align="right">{formatMoneyCurrency(booked_margin_amount, currency_id, 2)}</td>
            {!is_billed && billed_margin_amount !== null && <td>&nbsp;</td>}
          </tr>}
        </tbody>
      </table>
    );

    return {
      amount: margin_amount,
      currency_id,
      tooltipText,
    };
  },
};

const MarginPercentageColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Percentage
> = {
  header: "Margin %",
  type: BASE_COLUMN_TYPES.Percentage,
  fields: ["order_type", "booked_margin_percent", "billed_margin_percent", "is_billed"],
  accessorKey: "margin_percent",
  canSort: true,
  orderByField: "margin_percent",
  percentageOptions: { decimalPlaces: 2 },
  width: 91,
  dataTransform: ({ order_type, booked_margin_percent, billed_margin_percent, is_billed }) => {
    const percentage = "INVOICE" === order_type && is_billed ?
      billed_margin_percent :
      booked_margin_percent;

    const tooltipText = (billed_margin_percent !== null || booked_margin_percent !== null) && (
      <table>
        <tbody>
          {billed_margin_percent !== null && (
            <tr>
              <td>Billed Margin:</td>
              <td align="right">{formatPercent(billed_margin_percent, 2)}</td>
              {!is_billed && <td>(Partial)</td>}
            </tr>
          )}
          {booked_margin_percent !== null && <tr>
            <td>Booked Margin:</td>
            <td align="right">{formatPercent(booked_margin_percent, 2)}</td>
            {!is_billed && billed_margin_percent !== null && <td>&nbsp;</td>}
          </tr>}
        </tbody>
      </table>
    );

    return {
      percentage,
      tooltipText,
    };
  },
};

const NotesColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Notes> = {
  header: "Notes",
  type: BASE_COLUMN_TYPES.Notes,
  fields: ["num_notes", "job_number"],
  accessorKey: "notes",
  canSort: true,
  orderByField: "num_notes",
  width: 64,
  dataTransform: ({ num_notes, job_number }) => ({
    count: num_notes,
    href: `/project/${job_number}`,
  }),
};

export const Columns: ReportColumnConfigBase<BaseColumnType>[] = [
  ProjectNumberColumn,
  CRepColumn,
  ORepColumn,
  ProjectNameColumn,
  ClientColumn,
  DateColumn,
  BudgetColumn,
  SubtotalColumn,
  MarginColumn,
  MarginPercentageColumn,
  StatusColumn,
  NotesColumn,
];
